import {
    createContext, useEffect, useMemo, useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import Preloader from "../../components/Preloader";
import { useStudents } from "../students";
import { useGetStudentVerificationStatus } from "../../hooks/useGetStudentVerificationStatus";
import { useGetPrimaryContactVerificationStatus } from "../../hooks/useGetPrimaryContactVerificationStatus";
import { useGetGeneralStatuses } from "../../hooks/useGetGeneralStatuses";
import { isVersionUK } from "../../services/versionService";
import axiosManager from "../../services/axiosManager";
import LockingConfirmationDialog from "../../components/LockingConfirmationDialog";
import { STUDENT_STATUS } from "../../constants/student.status";
import { applicantStatusHelper } from "../../helpers/applicant.status.helper";
import { useGetRenewalStatuses } from "../../hooks/useGetRenewalStatuses";

const StatusesContext = createContext(undefined);

const { Provider } = StatusesContext;

const VerificationStatusProvider = ({ children }) => {
    const { activeStudentId, activeStudent } = useStudents();

    const studentStatus = useMemo(() => activeStudent?.studentStatusCode, [activeStudent?.studentStatusCode]);
    const isApplicant = useMemo(() => [STUDENT_STATUS.APPLICANT, STUDENT_STATUS.RETURNER].includes(studentStatus), [studentStatus]);

    const {
        isLoading: isLoadingGeneralStatuses,
        isRefetching: isRefetchingGeneralStatuses,
        refetch: refetchGeneralStatuses, general: generalStatuses,
    } = useGetGeneralStatuses(activeStudentId, !!activeStudentId && activeStudentId !== "new");

    const {
        isLoading: isLoadingVerificationStatus,
        isRefetching: isRefetchingVerificationStatus,
        refetch: refetchVerificationStatus,
        studentVerificationStatus, studentVerificationKey,
    } = useGetStudentVerificationStatus(activeStudentId, isApplicant);

    const {
        isLoading: isLoadingPrimaryContactVerificationStatus,
        isRefetching: isRefetchingPrimaryContactVerificationStatus,
        refetch: refetchPrimaryContactVerificationStatus,
        primaryContactVerificationStatus, primaryContactVerificationKey,
    } = useGetPrimaryContactVerificationStatus(activeStudentId, isApplicant);

    const {
        isLoading: isLoadingRenewalStatus,
        isRefetching: isRefetchingRenewalStatus,
        refetch: refetchRenewalStatus, renewalStatus,
    } = useGetRenewalStatuses(activeStudentId, !!activeStudentId && !isApplicant);

    const {
        isLoading: isLoadingContact, data: contact,
        isRefetching: isRefetchingContact, refetch: refetchContact,
    } = useQuery({
        queryKey: ["parent/contacts"],
        queryFn: () => axiosManager.get$("parent/api/Contacts").then((response) => response?.data),
        initialData: {},
        retry: 0,
    });

    const isLoading = isLoadingGeneralStatuses || isRefetchingGeneralStatuses || isLoadingVerificationStatus || isRefetchingVerificationStatus
        || isLoadingPrimaryContactVerificationStatus || isRefetchingPrimaryContactVerificationStatus || isLoadingContact || isRefetchingContact || isLoadingRenewalStatus || isRefetchingRenewalStatus;

    const { studyingIb, hasSafeguardingNeed } = generalStatuses?.student || {};

    const hasContactFullInfo = useMemo(() => applicantStatusHelper.hasContactFullDetails(contact), [contact]);
    const hideSubjectSelectionUS = useMemo(() => applicantStatusHelper.hideSubjectSelectionUS(generalStatuses, generalStatuses.student), [generalStatuses]);
    const showConsultationCall = useMemo(() => applicantStatusHelper.showConsultationCall(hideSubjectSelectionUS, generalStatuses.student), [generalStatuses]);
    const isRequiredDBS = useMemo(() => applicantStatusHelper.isRequiredDBS(generalStatuses.student), [generalStatuses]);
    const isRequiredOutOfYear = useMemo(() => applicantStatusHelper.isRequiredOutOfYear(generalStatuses.student), [generalStatuses]);
    const isSchoolReportsRequired = useMemo(() => applicantStatusHelper.isSchoolReportsRequired(generalStatuses.student), [generalStatuses]);

    const statuses = useMemo(() => applicantStatusHelper.calculateApplicantStatuses({
        hasContactFullInfo,
        primaryContactVerificationKey,
        generalStatuses,
        studentVerificationKey,
        hideSubjectSelectionUS,
        showConsultationCall,
        isSchoolReportsRequired,
        isRequiredDBS,
        isRequiredOutOfYear,
        hasSafeguardingNeed,
        studyingIb,
    }), [hasContactFullInfo, primaryContactVerificationKey, studentVerificationKey, generalStatuses]);
    const isAdultsAndDetailsAndEducationComplete = useMemo(() => applicantStatusHelper.checkAdultsAndDetailsAndEducationComplete(statuses), [statuses]);
    const isLockedStatus = useMemo(() => applicantStatusHelper.calculateLockedStatus({
        statuses,
        generalStatuses,
        hideSubjectSelectionUS,
        isAdultsAndDetailsAndEducationComplete,
        showConsultationCall,
    }), [generalStatuses, showConsultationCall]);
    const stepsProgress = useMemo(() => applicantStatusHelper.calculateStepsProgress(statuses), [statuses]);
    const showRenewal = useMemo(() => renewalStatus?.isB2B === false && (!!renewalStatus?.awaitingPayment || !!renewalStatus?.isRenewalsOpen), [renewalStatus]);

    const showMentorPathway = useMemo(() => activeStudent?.mentorPathwayStartDate
        && activeStudent?.mentorPathwayFinishDate
        && new Date(activeStudent?.mentorPathwayStartDate) <= new Date()
        && new Date(activeStudent?.mentorPathwayFinishDate) >= new Date(), [activeStudent]);

    const isLockingConfirmationOpen = isAdultsAndDetailsAndEducationComplete && isVersionUK && !generalStatuses?.student?.enrolmentDetailsLocked;

    const [isOpenLockingModal, setIsOpenLockingModal] = useState(isLockingConfirmationOpen);

    useEffect(() => {
        if (isLockingConfirmationOpen) setIsOpenLockingModal(true);
    }, [isLockingConfirmationOpen]);

    const value = {
        isApplicant,
        studentStatus,
        studentIsamsId: generalStatuses?.student?.isamsId,
        refetchGeneralStatuses,
        generalStatuses,
        refetchVerificationStatus,
        studentVerificationStatus,
        studentVerificationKey,
        refetchPrimaryContactVerificationStatus,
        primaryContactVerificationStatus,
        primaryContactVerificationKey,
        statuses,
        isLockedStatus,
        stepsProgress,
        showConsultationCall,
        isRequiredDBS,
        isRequiredOutOfYear,
        isSchoolReportsRequired,
        contact,
        refetchContact,
        isAdultsAndDetailsAndEducationComplete,
        hideSubjectSelectionUS,
        isLoadingGeneralStatuses,
        showRenewal,
        refetchRenewalStatus,
        renewalStatus,
        isLockingConfirmationOpen,
        setIsOpenLockingModal,
        showMentorPathway,
    };

    return (
        <Preloader isLoading={isLoading} overflow showInitiallyOverflow>
            <Provider value={value}>
                {children}
                {(isLockingConfirmationOpen) && (
                    <LockingConfirmationDialog
                        isOpen={isOpenLockingModal}
                        setIsOpen={setIsOpenLockingModal}
                    />
                )}
            </Provider>
        </Preloader>
    );
};

export {
    VerificationStatusProvider,
    StatusesContext,
};
